import { parseHandleToken } from '@features/flows/lib/autocast/edge-context';
import { composeHandleId, parseHandleId } from '@features/flows/utils/edge';
import { deriveCoarseTypeFromPin, type PinSpecLike } from '@features/flows/utils/pins';

export type NodePinsSnapshot =
  | { data?: { in?: PinSpecLike[]; out?: PinSpecLike[] } }
  | undefined;

export type ComputeConnectionMetaArgs = {
  params: {
    source?: string;
    target?: string;
    sourceHandle?: string | null | undefined;
    targetHandle?: string | null | undefined;
    id?: string | undefined;
    data?: Record<string, unknown> | undefined;
  };
  pending?: { kind?: 'data' | 'exec' } | null | undefined;
  sourcePins: NodePinsSnapshot;
  targetPins: NodePinsSnapshot;
};

export type ConnectionMeta = {
  edgeChannel: 'data' | 'exec';
  rawSource?: string;
  rawTarget?: string;
  qualifiedSourceHandle: string;
  qualifiedTargetHandle: string;
  sourceCoarseAtConnect?: string;
  targetCoarseAtConnect?: string;
  coarseMismatch: boolean;
  shouldAutocast: boolean;
  typeLabel?: string;
};

export function computeConnectionMeta({
  params,
  pending,
  sourcePins,
  targetPins,
}: ComputeConnectionMetaArgs): ConnectionMeta {
  const looksData =
    (typeof params.sourceHandle === 'string' &&
      String(params.sourceHandle).startsWith('data:')) ||
    (typeof params.targetHandle === 'string' &&
      String(params.targetHandle).startsWith('data:'));

  const rawSource =
    parseHandleToken(params.sourceHandle, 'out') ?? undefined;
  const rawTarget =
    parseHandleToken(params.targetHandle, 'in') ?? undefined;

  const parsedSource = typeof params.sourceHandle === 'string' ? parseHandleId(params.sourceHandle) : null;
  const parsedTarget = typeof params.targetHandle === 'string' ? parseHandleId(params.targetHandle) : null;

  const srcOutPins = Array.isArray(sourcePins?.data?.out) ? (sourcePins!.data!.out as PinSpecLike[]) : [];
  const tgtInPins  = Array.isArray(targetPins?.data?.in) ? (targetPins!.data!.in as PinSpecLike[]) : [];

  const sourceIsData = srcOutPins.some((p) => p && (p as any).id === rawSource);
  const targetIsData = tgtInPins.some((p) => p && (p as any).id === rawTarget);

  const isDataConnection =
    (pending?.kind === 'data') ||
    parsedSource?.channel === 'data' ||
    parsedTarget?.channel === 'data' ||
    looksData ||
    sourceIsData ||
    targetIsData;

  const edgeChannel: 'data' | 'exec' = isDataConnection ? 'data' : 'exec';

  const srcPin = srcOutPins.find((p) => (p as any).id === rawSource);
  const tgtPin = tgtInPins.find((p) => (p as any).id === rawTarget);

  const sourceCoarseAtConnect = deriveCoarseTypeFromPin(srcPin);
  const targetCoarseAtConnect = deriveCoarseTypeFromPin(tgtPin);

  const coarseMismatch =
    !!sourceCoarseAtConnect &&
    !!targetCoarseAtConnect &&
    sourceCoarseAtConnect !== 'any' &&
    targetCoarseAtConnect !== 'any' &&
    sourceCoarseAtConnect !== targetCoarseAtConnect;

  const qualifiedSourceHandle = composeHandleId(edgeChannel, 'out', rawSource ?? 'out');
  const qualifiedTargetHandle = composeHandleId(edgeChannel, 'in', rawTarget ?? 'in');

  const typeLabel = coarseMismatch
    ? `${sourceCoarseAtConnect ?? 'any'}→${targetCoarseAtConnect ?? 'any'}`
    : (params.data as any)?.typeLabel;

  return {
    edgeChannel,
    rawSource,
    rawTarget,
    qualifiedSourceHandle,
    qualifiedTargetHandle,
    sourceCoarseAtConnect,
    targetCoarseAtConnect,
    coarseMismatch,
    shouldAutocast: isDataConnection,
    typeLabel,
  };
}
